import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wine, Beer, GlassWater, Flame } from "lucide-react";
import SEO from "../components/SEO";
import { useBookingModal } from "../context/BookingModalContext";

const menuSchema = {
  "@context": "https://schema.org",
  "@type": "Menu",
  "name": "Bar Spot Drinks Menu",
  "description": "Full drinks menu for mobile bar hire — draught beers, wines, spirits, cocktails and soft drinks at competitive prices.",
  "hasMenuSection": [
    { "@type": "MenuSection", "name": "Draughts", "description": "Camden Hells, Asahi, Madri, Guinness, Neck Oil and more" },
    { "@type": "MenuSection", "name": "Wines", "description": "Red, White, Rosé, Prosecco, Champagne" },
    { "@type": "MenuSection", "name": "Spirits", "description": "Vodka, Gin, Craft Gins, Whisky, Rum, Tequila, Brandy" },
    { "@type": "MenuSection", "name": "Shots", "description": "Tequila, Tequila Rose, Baby Guinness, Sambuca" }
  ]
};

const MenuItem = ({ name }) => (
  <li className="text-[#444444]">{name}</li>
);

const MenuCategory = ({ name, priceRange, icon, items, note, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
  >
    <div className="flex items-center justify-between mb-4 md:mb-6">
      <div className="flex items-center gap-3">
        <div className="text-[#a5dae0]">{icon}</div>
        <h3 className="text-xl md:text-2xl">{name}</h3>
      </div>
      {priceRange && (
        <span className="text-[#a5dae0] font-semibold text-sm md:text-base">{priceRange}</span>
      )}
    </div>
    <ul className="space-y-2 md:space-y-3 columns-1 md:columns-2 gap-x-6">
      {items.map((item, j) => (
        <MenuItem key={j} name={item} />
      ))}
    </ul>
    {note && <p className="text-[#888888] text-sm mt-4 italic">{note}</p>}
  </motion.div>
);

const MenuPage = () => {
  const { openBookingModal } = useBookingModal();
  const draughts = ["Camden Hells", "Asahi", "Brewdog Lost Lager", "Madri", "Guinness", "Neck Oil", "Camden Pale", "Brewdog Punk IPA", "Inches", "Brewdog Wingman", "Stowford Press"];
  const bottledCanned = ["Peroni", "Punk IPA", "Heineken 0%", "Doom Bar (Or similar)", "Fruity Ciders", "Variety of Crafts"];
  const wines = ["Red (Merlot)", "White (Sauvignon or Pinot)", "Rosé", "Prosecco", "Champagne"];
  const spirits = ["Vodka", "Gin", "Craft Gins", "Whisky", "Rum (White / Spiced)", "Tequila", "Brandy / Cognac"];
  const softs = ["Variety of options"];
  const shots = ["Tequila", "Tequila Rose", "Baby Guinness", "Sambuca", "Misc Spirit"];

  return (
    <div className="pt-20">
      <SEO
        title="Drinks Menu & Prices | Mobile Bar Hire | Bar Spot"
        description="View our full drinks menu — draught beers, wines, spirits, cocktails & soft drinks. Premium selection at competitive prices for your event."
        canonical="/menu"
        schema={menuSchema}
      />
      <section className="py-16 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 md:mb-16"
          >
            <p className="text-[#a5dae0] font-script text-xl md:text-2xl mb-2 md:mb-4">Our Selection</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl mb-4 md:mb-6">Drinks Menu</h1>
            <p className="text-base md:text-lg text-[#555555] max-w-2xl mx-auto px-4">
              Premium selection of beers, wines, spirits, and more.
              Custom menus available for your event.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <MenuCategory name="Draughts" priceRange="£6 - £6.80" icon={<Beer size={24} />} items={draughts} note="Other brands available on request" delay={0} />
            <MenuCategory name="Bottled & Canned" priceRange="£4 - £5.50" icon={<Beer size={24} />} items={bottledCanned} delay={0.1} />
            <MenuCategory name="Wines" priceRange="£5.50 - £7" icon={<Wine size={24} />} items={wines} delay={0.2} />
            <MenuCategory name="Spirits" priceRange="£6.50 - £8.50" icon={<GlassWater size={24} />} items={spirits} note="Other options available on request" delay={0.3} />
            <MenuCategory name="Softs" priceRange="£2 - £3" icon={<GlassWater size={24} />} items={softs} delay={0.4} />
            <MenuCategory name="Shots" priceRange="from £5" icon={<Flame size={24} />} items={shots} delay={0.5} />
          </div>

          {/* CTA to booking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12 md:mt-16"
          >
            <p className="text-[#555555] mb-4">Like what you see? Get a personalised quote for your event.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={openBookingModal} className="bg-[#a5dae0] text-[#1a1a1a] px-8 py-3 rounded-full font-semibold hover:bg-[#8bc9d0] transition-all text-sm">Get Your Quote</button>
              <Link to="/weddings" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Wedding Packages</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
